import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './AppointmentReminder.css';

function AppointmentReminder({ userId }) {
  const [reminders, setReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { modal, openModal, closeModal } = useFeedbackModal();
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null
  });
  const [formData, setFormData] = useState({
    appointmentType: '',
    appointmentName: '',
    scheduledDate: '',
    scheduledTime: '',
    reminderDate: '',
    reminderTime: '',
    notes: '',
    trimester: '',
    pregnancyWeek: '',
  });

  const loadReminders = useCallback(async () => {
    try {
      setLoading(true);
      const [all, upcoming] = await Promise.all([
        api.getUserReminders(userId),
        api.getUpcomingReminders(userId),
      ]);
      setReminders(all);
      setUpcomingReminders(upcoming);
    } catch (error) {
      console.error('Error loading reminders:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadReminders();
    }
  }, [userId, loadReminders]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await api.createAppointmentReminder({
        ...formData,
        userId,
        scheduledDate: formData.scheduledDate || null,
        scheduledTime: formData.scheduledTime ? `${formData.scheduledDate}T${formData.scheduledTime}` : null,
        reminderDate: formData.reminderDate || null,
        reminderTime: formData.reminderTime ? `${formData.reminderDate}T${formData.reminderTime}` : null,
        trimester: formData.trimester ? parseInt(formData.trimester) : null,
        pregnancyWeek: formData.pregnancyWeek ? parseInt(formData.pregnancyWeek) : null,
      });
      setShowForm(false);
      setFormData({
        appointmentType: '',
        appointmentName: '',
        scheduledDate: '',
        scheduledTime: '',
        reminderDate: '',
        reminderTime: '',
        notes: '',
        trimester: '',
        pregnancyWeek: '',
      });
      loadReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
      openModal({
        title: 'Create failed',
        message: 'Failed to create reminder.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const markCompleted = async (reminderId) => {
    try {
      await api.markReminderCompleted(reminderId, userId);
      loadReminders();
    } catch (error) {
      console.error('Error marking reminder as completed:', error);
    }
  };

  const deleteReminder = async (reminderId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete reminder',
      message: 'Are you sure you want to delete this reminder?',
      type: 'warning',
      onConfirm: async () => {
        try {
          await api.deleteReminder(reminderId, userId);
          loadReminders();
        } catch (error) {
          console.error('Error deleting reminder:', error);
          openModal({
            title: 'Delete failed',
            message: 'Failed to delete reminder.',
            type: 'error'
          });
        }
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    return new Date(dateTimeString).toLocaleString();
  };

  return (
    <div className="appointment-reminder">
      <div className="reminder-header">
        <h2>📅 Appointment Reminders</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? '❌ Cancel' : '➕ Add Reminder'}
        </button>
      </div>

      {showForm && (
        <form className="reminder-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Appointment Type *</label>
              <input
                type="text"
                required
                value={formData.appointmentType}
                onChange={(e) => setFormData({ ...formData, appointmentType: e.target.value })}
                placeholder="e.g., Prenatal Checkup, Ultrasound, Blood Test"
              />
            </div>
            <div className="form-group">
              <label>Appointment Name</label>
              <input
                type="text"
                value={formData.appointmentName}
                onChange={(e) => setFormData({ ...formData, appointmentName: e.target.value })}
                placeholder="Optional name for this appointment"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Scheduled Date *</label>
              <input
                type="date"
                required
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Scheduled Time</label>
              <input
                type="time"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Reminder Date</label>
              <input
                type="date"
                value={formData.reminderDate}
                onChange={(e) => setFormData({ ...formData, reminderDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Reminder Time</label>
              <input
                type="time"
                value={formData.reminderTime}
                onChange={(e) => setFormData({ ...formData, reminderTime: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Trimester</label>
              <select
                value={formData.trimester}
                onChange={(e) => setFormData({ ...formData, trimester: e.target.value })}
              >
                <option value="">Select trimester</option>
                <option value="1">First Trimester</option>
                <option value="2">Second Trimester</option>
                <option value="3">Third Trimester</option>
              </select>
            </div>
            <div className="form-group">
              <label>Pregnancy Week</label>
              <input
                type="number"
                min="1"
                max="40"
                value={formData.pregnancyWeek}
                onChange={(e) => setFormData({ ...formData, pregnancyWeek: e.target.value })}
                placeholder="Week number"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="Additional notes about this appointment..."
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : '💾 Save Reminder'}
          </button>
        </form>
      )}

      {upcomingReminders.length > 0 && (
        <div className="upcoming-reminders">
          <h3>⏰ Upcoming Appointments</h3>
          <div className="reminders-list">
            {upcomingReminders.map(reminder => (
              <div key={reminder.id} className={`reminder-card ${reminder.completed ? 'completed' : ''}`}>
                <div className="reminder-content">
                  <div className="reminder-title">
                    <h4>{reminder.appointmentType}</h4>
                    {reminder.appointmentName && (
                      <span className="reminder-name">{reminder.appointmentName}</span>
                    )}
                  </div>
                  <div className="reminder-dates">
                    {reminder.scheduledDate && (
                      <div>📅 {formatDate(reminder.scheduledDate)}</div>
                    )}
                    {reminder.scheduledTime && (
                      <div>⏰ {formatDateTime(reminder.scheduledTime)}</div>
                    )}
                    {reminder.reminderDate && (
                      <div>🔔 Reminder: {formatDate(reminder.reminderDate)}</div>
                    )}
                  </div>
                  {reminder.notes && (
                    <p className="reminder-notes">{reminder.notes}</p>
                  )}
                </div>
                <div className="reminder-actions">
                  {!reminder.completed && (
                    <button
                      className="btn-complete"
                      onClick={() => markCompleted(reminder.id)}
                    >
                      ✅ Complete
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => deleteReminder(reminder.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {reminders.length > 0 && (
        <div className="all-reminders">
          <h3>📋 All Appointments</h3>
          <div className="reminders-list">
            {reminders.map(reminder => (
              <div key={reminder.id} className={`reminder-card ${reminder.completed ? 'completed' : ''}`}>
                <div className="reminder-content">
                  <div className="reminder-title">
                    <h4>{reminder.appointmentType}</h4>
                    {reminder.appointmentName && (
                      <span className="reminder-name">{reminder.appointmentName}</span>
                    )}
                    {reminder.completed && <span className="completed-badge">✅ Completed</span>}
                  </div>
                  <div className="reminder-dates">
                    {reminder.scheduledDate && (
                      <div>📅 {formatDate(reminder.scheduledDate)}</div>
                    )}
                    {reminder.scheduledTime && (
                      <div>⏰ {formatDateTime(reminder.scheduledTime)}</div>
                    )}
                  </div>
                  {reminder.notes && (
                    <p className="reminder-notes">{reminder.notes}</p>
                  )}
                </div>
                <div className="reminder-actions">
                  {!reminder.completed && (
                    <button
                      className="btn-complete"
                      onClick={() => markCompleted(reminder.id)}
                    >
                      ✅ Complete
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => deleteReminder(reminder.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && reminders.length === 0 && (
        <div className="no-reminders">
          <p>No appointment reminders yet. Add one to get started!</p>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm || (() => {})}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Continue"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default AppointmentReminder;
















